import React, { createContext, useContext, useEffect, useState } from 'react'
import { CollectionId, getRecord } from 'utils/firestore'

// Per-user feature keys. Unlike the environment-based flags in
// utils/feature-flags.ts (which enable a feature for a whole environment),
// these gate features for individual users via the `enabledFeatures` array on
// their Firestore `users/{uid}` document.
//
// To grant a user access to a feature, add its key to that user's
// `enabledFeatures` array in the Firebase console.
//
// Style (matches utils/feature-flags.ts): no whitespace, all lower case, only
// ASCII, use _ not -.
export const JOG_HOMING_FEATURE = 'jog_homing'

type UserFeaturesContextValue = {
  enabledFeatures: string[]
  isLoading: boolean
  hasFeature: (feature: string) => boolean
}

const UserFeaturesContext = createContext<UserFeaturesContextValue>({
  enabledFeatures: [],
  isLoading: true,
  hasFeature: () => false
})

export function UserFeaturesProvider({
  userId,
  children
}: {
  userId: string
  children: React.ReactNode
}) {
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    getRecord(CollectionId.Users, userId).then((doc) => {
      setEnabledFeatures(doc?.enabledFeatures ?? [])
      setIsLoading(false)
    })
  }, [userId])

  const hasFeature = (feature: string) => enabledFeatures.includes(feature)

  return (
    <UserFeaturesContext.Provider value={{ enabledFeatures, isLoading, hasFeature }}>
      {children}
    </UserFeaturesContext.Provider>
  )
}

export const useUserFeatures = () => useContext(UserFeaturesContext)
